import { Fragment } from 'react';
import { ChevronDown, ChevronRight, UserPlus, FileBadge, KeyRound } from 'lucide-react';
import { DATE_COLS, STATE_STYLE, STATUS_BADGE, dateState, fmtDate, daysText } from './utils';

const DateCell = ({ iso }) => {
  const { state, days } = dateState(iso);
  return (
    <span className={`inline-flex flex-col rounded-md border px-1.5 py-0.5 text-[11px] leading-tight ${STATE_STYLE[state]}`} title={daysText(days)}>
      {iso ? fmtDate(iso) : 'yok'}
      {iso && state !== 'ok' && <span className="font-semibold">{daysText(days)}</span>}
    </span>
  );
};

const Bar = ({ pct }) => (
  <div className="flex items-center gap-1.5 min-w-[80px]">
    <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div className={`h-full ${pct === 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
    </div>
    <span className="text-[11px] font-semibold tabular-nums w-8 text-right">{pct}%</span>
  </div>
);

const Row = ({ r, open, onToggle, onAssign, onVergunning }) => {
  const st = STATUS_BADGE[r.status] || STATUS_BADGE.ACTIVE;
  const rental = r.ownership_type === 'Kiralık';
  return (
    <tr className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 ${open ? 'bg-sky-50/50 dark:bg-sky-950/20' : ''}`} data-testid={`vehicle-overview-row-${r.id}`}>
      <td className="px-2 py-2">
        <button type="button" onClick={() => onToggle(r.id)} className="text-slate-400 hover:text-slate-700" data-testid={`vehicle-row-toggle-${r.id}`}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </td>
      <td className="px-2 py-2">
        <div className="font-mono font-bold text-slate-900 dark:text-white">{r.license_plate}</div>
        <div className="text-[11px] text-slate-500 truncate max-w-[160px]">{[r.brand, r.model].filter(Boolean).join(' ') || '—'}{r.firm_name ? ` · ${r.firm_name}` : ''}</div>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1">
          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
          {r.ownership_type && <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${rental ? 'bg-sky-100 text-sky-700 border-sky-300' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{r.ownership_type}</span>}
        </div>
        {rental && (
          <div className="text-[11px] text-sky-700 dark:text-sky-300 mt-0.5 flex items-center gap-1">
            <KeyRound className="h-3 w-3" />{r.replaces_plate ? `${r.replaces_plate} yerine · ` : ''}dönüş {r.rental_end_date ? fmtDate(r.rental_end_date) : '?'}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        {r.current_drivers.length ? (
          <button type="button" onClick={() => onAssign(r)} className="text-left text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-sky-700" data-testid={`vehicle-driver-${r.id}`}>
            {r.current_drivers.map((d) => d.full_name).join(', ')}
          </button>
        ) : (
          <button type="button" onClick={() => onAssign(r)} disabled={r.status !== 'ACTIVE'}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-sky-400 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`vehicle-assign-btn-${r.id}`}>
            <UserPlus className="h-3.5 w-3.5" />Şoför ata
          </button>
        )}
      </td>
      <td className="px-2 py-2">
        <button type="button" onClick={() => onVergunning(r)} className="flex flex-wrap gap-1 items-center" data-testid={`vehicle-vergunning-btn-${r.id}`}>
          {r.vergunning_codes.length ? r.vergunning_codes.map((c) => (
            <span key={c} className="rounded bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-0.5 text-[10px] font-bold">{c}{r.vergunning_types?.[c] === 'COPY' ? ' (K)' : ''}</span>
          )) : <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-indigo-300 px-2 py-0.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"><FileBadge className="h-3.5 w-3.5" />Ata</span>}
        </button>
      </td>
      {DATE_COLS.map((c) => <td key={c.key} className="px-2 py-2"><DateCell iso={r[c.key]} /></td>)}
      <td className="px-2 py-2">
        {(() => {
          const all = [...r.missing_items.filter((m) => m !== 'Şoför' && m !== 'Vergunning'), ...r.missing_fields];
          return (
            <div className="flex flex-wrap gap-1 max-w-[220px]">
              {all.slice(0, 4).map((m) => (
                <span key={m} className="rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 text-[10px]">{m}</span>
              ))}
              {all.length > 4 && <span className="text-[10px] text-slate-400" data-testid={`vehicle-missing-more-${r.id}`}>+{all.length - 4}</span>}
            </div>
          );
        })()}
      </td>
      <td className="px-2 py-2"><Bar pct={r.completion} /></td>
    </tr>
  );
};

export function VehicleOverviewTable({ rows, expanded, onToggle, onAssign, onVergunning, renderEditor }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="vehicle-overview-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-8" />
            <th className="px-2 py-2 text-left">Plaka</th>
            <th className="px-2 py-2 text-left">Durum / Tür</th>
            <th className="px-2 py-2 text-left">Şoför</th>
            <th className="px-2 py-2 text-left">Vergunning</th>
            {DATE_COLS.map((c) => <th key={c.key} className="px-2 py-2 text-left">{c.label}</th>)}
            <th className="px-2 py-2 text-left">Eksik bilgi</th>
            <th className="px-2 py-2 text-left">Tamamlanma</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-slate-400">Kayıt yok</td></tr>}
          {rows.map((r) => (
            <Fragment key={r.id}>
              <Row r={r} open={expanded === r.id} onToggle={onToggle} onAssign={onAssign} onVergunning={onVergunning} />
              {expanded === r.id && <tr><td colSpan={10}>{renderEditor(r)}</td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
